import { useState, useEffect } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import Sidebar from './Sidebar'
import TopBar from './TopBar'
import AIChatPanel from './AIChatPanel'
import WelcomeBanner from '../onboarding/WelcomeBanner'
import OnboardingTour from '../onboarding/OnboardingTour'
import HelpCenter from '../onboarding/HelpCenter'
import { useAuth } from '../../hooks/useAuth'

export default function AppLayout() {
  const { user } = useAuth()
  const location = useLocation()
  const [chatOpen, setChatOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)

  // Detect if we're on the dedicated AI Chat page
  const isAIChatPage = location.pathname === '/ai-chat'

  // Auto-close sidebar chat when navigating to the full AI Chat page
  useEffect(() => {
    if (isAIChatPage && chatOpen) {
      setChatOpen(false)
    }
  }, [isAIChatPage])

  useEffect(() => {
    setMobileSidebarOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (!mobileSidebarOpen) return

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setMobileSidebarOpen(false)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [mobileSidebarOpen])

  const handleToggleSidebar = () => {
    if (window.matchMedia('(min-width: 1024px)').matches) {
      setSidebarCollapsed((collapsed) => !collapsed)
      return
    }

    setMobileSidebarOpen((open) => !open)
  }

  // Global onboarding state
  const [showWelcome, setShowWelcome] = useState(() => {
    return !localStorage.getItem('ss-onboarding-completed')
  })
  const [showTour, setShowTour] = useState(false)
  const [showHelp, setShowHelp] = useState(false)

  const handleStartTour = () => {
    setShowWelcome(false)
    setShowTour(true)
  }

  const handleTourComplete = () => {
    setShowTour(false)
    localStorage.setItem('ss-onboarding-completed', 'true')
  }

  const handleDismissWelcome = () => {
    setShowWelcome(false)
    localStorage.setItem('ss-onboarding-completed', 'true')
  }

  const handleOpenHelp = () => {
    setShowHelp(true)
  }

  const handleRestartTour = () => {
    setShowHelp(false)
    setShowTour(true)
  }

  return (
    <div className="h-dvh flex flex-col overflow-hidden bg-surface-100 dark:bg-surface-900">
      <TopBar 
        onToggleSidebar={handleToggleSidebar}
        onOpenHelp={handleOpenHelp}
        isAIChatPage={isAIChatPage}
      />
      
      <div className="flex flex-1 overflow-hidden">
        {/* Left Sidebar */}
        <div className="hidden lg:flex">
          <Sidebar collapsed={sidebarCollapsed} />
        </div>

        {mobileSidebarOpen && (
          <div className="fixed inset-x-0 bottom-0 top-14 z-40 flex lg:hidden">
            <Sidebar onNavigate={() => setMobileSidebarOpen(false)} />
            <button
              type="button"
              className="flex-1 bg-surface-950/40 backdrop-blur-[2px]"
              onClick={() => setMobileSidebarOpen(false)}
              aria-label="Close navigation menu"
            />
          </div>
        )}
        
        {/* Main Content */}
        <main className="flex-1 overflow-auto transition-all duration-300">
          {isAIChatPage ? (
            /* AI Chat page gets full bleed - no padding wrapper */
            <Outlet />
          ) : (
            <div className="p-4 sm:p-6 xl:p-8">
              {/* Welcome Banner for New Users */}
              {showWelcome && (
                <WelcomeBanner
                  userName={user?.display_name?.split(' ')[0]}
                  onStartTour={handleStartTour}
                  onDismiss={handleDismissWelcome}
                />
              )}
              <Outlet />
            </div>
          )}
        </main>
        
        {/* AI Chat Panel - hidden on the dedicated AI Chat page */}
        {!isAIChatPage && (
          <AIChatPanel 
            isOpen={chatOpen} 
            onClose={() => setChatOpen(false)} 
          />
        )}
      </div>

      {/* Global Onboarding Overlays */}
      {showTour && <OnboardingTour onComplete={handleTourComplete} />}
      <HelpCenter 
        isOpen={showHelp} 
        onClose={() => setShowHelp(false)} 
        onRestartTour={handleRestartTour}
      />
    </div>
  )
}
